import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft, EyeOff, Eye, Trash2, CheckSquare, Square } from "lucide-react";

export default function AdminShowEpisodes() {
  const { id } = useParams();
  const [show, setShow] = useState(null);
  const [episodes, setEpisodes] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [season, setSeason] = useState("all");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: s }, { data: e }] = await Promise.all([
        api.get(`/shows/${id}`),
        api.get(`/shows/${id}/episodes?include_hidden=true`),
      ]);
      setShow(s); setEpisodes(e);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [id]);

  const seasons = useMemo(() => [...new Set(episodes.map((e) => e.season_number))].sort((a, b) => a - b), [episodes]);
  const visible = useMemo(() => season === "all" ? episodes : episodes.filter((e) => e.season_number === Number(season)), [episodes, season]);

  const toggle = (eid) => {
    const s = new Set(selected); s.has(eid) ? s.delete(eid) : s.add(eid); setSelected(s);
  };
  const toggleAll = () => {
    if (visible.every((e) => selected.has(e.id))) {
      const s = new Set(selected); visible.forEach((e) => s.delete(e.id)); setSelected(s);
    } else {
      const s = new Set(selected); visible.forEach((e) => s.add(e.id)); setSelected(s);
    }
  };
  const selectSeason = (n) => {
    const s = new Set(selected);
    episodes.filter((e) => e.season_number === n).forEach((e) => s.add(e.id));
    setSelected(s);
  };

  const bulk = async (action) => {
    if (selected.size === 0) return;
    const ids = Array.from(selected);
    try {
      let msg = "";
      if (action === "hide") {
        const { data } = await api.post("/episodes/bulk-hide", { episode_ids: ids, hidden: true });
        msg = `${data.modified} hidden`;
      } else if (action === "unhide") {
        const { data } = await api.post("/episodes/bulk-hide", { episode_ids: ids, hidden: false });
        msg = `${data.modified} unhidden`;
      } else if (action === "delete") {
        if (!window.confirm(`Delete ${ids.length} episode(s) permanently? Files on disk are kept, but StreamHoard entries and HLS output are removed.`)) return;
        const { data } = await api.post("/episodes/bulk-delete", { episode_ids: ids });
        msg = `${data.deleted} deleted`;
      }
      toast.success(msg);
      setSelected(new Set()); load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk action failed");
    }
  };

  if (!show) return <div className="min-h-screen bg-[#050505] flex items-center justify-center text-[#8A8A8A]">Loading…</div>;

  const allSelected = visible.length > 0 && visible.every((e) => selected.has(e.id));

  return (
    <div className="min-h-screen bg-[#050505] pt-32 pb-32" data-testid={`show-episodes-admin-${show.id}`}>
      <div className="px-6 md:px-12 max-w-[1500px] mx-auto">
        <Link to="/admin" className="inline-flex items-center gap-2 text-[#8A8A8A] hover:text-white text-xs uppercase tracking-[0.2em] mb-6" data-testid="back-to-admin">
          <ArrowLeft size={14} strokeWidth={1.5} /> Back to Admin
        </Link>
        <span className="text-xs uppercase tracking-[0.3em] text-[#D9381E]">Season Packs</span>
        <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter text-white mt-2" data-testid="admin-show-title">{show.title}</h1>
        <p className="text-[#8A8A8A] mt-3">{episodes.length} episodes across {seasons.length} season(s). Select any to bulk hide or delete.</p>

        {/* Season filter chips */}
        <div className="mt-8 flex flex-wrap items-center gap-2">
          <span className="text-[10px] uppercase tracking-[0.3em] text-[#8A8A8A] mr-2">View</span>
          <button onClick={() => setSeason("all")} className={`text-xs px-4 py-2 border transition-colors ${season === "all" ? "border-[#D9381E] text-white bg-[#D9381E]/10" : "border-[#222] text-[#8A8A8A] hover:text-white"}`} data-testid="season-all">All</button>
          {seasons.map((n) => (
            <div key={n} className="flex items-center gap-1">
              <button onClick={() => setSeason(n)} className={`text-xs px-4 py-2 border transition-colors ${season === n ? "border-[#D9381E] text-white bg-[#D9381E]/10" : "border-[#222] text-[#8A8A8A] hover:text-white"}`} data-testid={`filter-season-${n}`}>Season {n}</button>
              <button onClick={() => selectSeason(n)} className="text-[10px] uppercase tracking-[0.2em] text-[#8A8A8A] hover:text-[#D9381E] transition-colors px-1" title={`Select all S${n} episodes`} data-testid={`select-season-${n}`}>+all</button>
            </div>
          ))}
        </div>

        {/* Table header */}
        <div className="mt-8 border border-[#222]">
          <div className="grid grid-cols-12 items-center px-4 py-3 border-b border-[#222] text-[10px] uppercase tracking-[0.3em] text-[#8A8A8A]">
            <button onClick={toggleAll} className="col-span-1 flex items-center gap-2 text-[#8A8A8A] hover:text-white" data-testid="toggle-all-visible">
              {allSelected ? <CheckSquare size={16} strokeWidth={1.5} /> : <Square size={16} strokeWidth={1.5} />}
            </button>
            <span className="col-span-1">S·E</span>
            <span className="col-span-6">Title</span>
            <span className="col-span-3">Source</span>
            <span className="col-span-1 text-right">State</span>
          </div>
          {visible.length === 0 && <div className="p-8 text-center text-[#8A8A8A] text-sm">No episodes.</div>}
          {visible.map((ep) => (
            <div key={ep.id} className={`grid grid-cols-12 items-center px-4 py-3 border-b border-[#222] last:border-b-0 hover:bg-[#0F0F0F] transition-colors ${ep.hidden ? "opacity-50" : ""}`} data-testid={`admin-ep-row-${ep.id}`}>
              <button onClick={() => toggle(ep.id)} className="col-span-1 flex items-center text-[#8A8A8A] hover:text-white" data-testid={`select-ep-${ep.id}`}>
                {selected.has(ep.id) ? <CheckSquare size={16} strokeWidth={1.5} className="text-[#D9381E]" /> : <Square size={16} strokeWidth={1.5} />}
              </button>
              <span className="col-span-1 text-[10px] uppercase tracking-[0.2em] text-[#8A8A8A]">S{ep.season_number}·E{ep.episode_number}</span>
              <div className="col-span-6 min-w-0"><p className="text-white truncate">{ep.title || `Episode ${ep.episode_number}`}</p></div>
              <span className="col-span-3 text-[10px] uppercase tracking-[0.2em] text-[#8A8A8A]">{ep.storage_type}</span>
              <span className="col-span-1 text-right text-[10px] uppercase tracking-[0.2em]">{ep.hidden ? <span className="text-[#fca5a5]">hidden</span> : <span className="text-[#86efac]">live</span>}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Sticky bulk action bar */}
      {selected.size > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-40 bg-[#0F0F0F] border-t border-[#D9381E] px-6 md:px-12 py-4 flex flex-wrap items-center gap-3" data-testid="bulk-action-bar">
          <span className="text-xs uppercase tracking-[0.3em] text-[#D9381E]">{selected.size} selected</span>
          <div className="flex-1 flex flex-wrap gap-2 justify-end">
            <button onClick={() => bulk("hide")} className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] border border-[#222] hover:border-white text-[#8A8A8A] hover:text-white px-4 py-2" data-testid="bulk-hide">
              <EyeOff size={14} strokeWidth={1.5} /> Hide
            </button>
            <button onClick={() => bulk("unhide")} className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] border border-[#222] hover:border-white text-[#8A8A8A] hover:text-white px-4 py-2" data-testid="bulk-unhide">
              <Eye size={14} strokeWidth={1.5} /> Unhide
            </button>
            <button onClick={() => bulk("delete")} className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] border border-[#222] hover:border-[#fca5a5] hover:text-[#fca5a5] text-[#8A8A8A] px-4 py-2" data-testid="bulk-delete">
              <Trash2 size={14} strokeWidth={1.5} /> Delete
            </button>
            <button onClick={() => setSelected(new Set())} className="text-xs uppercase tracking-[0.2em] text-[#8A8A8A] hover:text-white px-4 py-2" data-testid="clear-selection">Clear</button>
          </div>
        </div>
      )}
    </div>
  );
}
